/**
 * Stable names for the student's handwriting.
 *
 * The notepad agent references canvas content by id. Typeset content carries its
 * own text, so the agent can find it in the screenshot by reading it; pen strokes
 * carry nothing, and (with OCR off) nothing ever tells the agent what a stroke
 * says. What it can do is match a NAME against a POSITION — but only if the name
 * survives from turn to turn. A label that renumbered whenever the student wrote
 * another line would break every reference the agent made in an earlier turn,
 * including the ones replayed to it verbatim as conversation history.
 *
 * So a label is minted once for a cluster of strokes and then follows those
 * strokes for the life of the canvas session:
 *
 *  - a cluster that GROWS (the student adds to the same line) keeps its label,
 *    because at least one member already carries it;
 *  - two clusters that MERGE into one adopt the older of the two labels, so the
 *    label with more history behind it is the one that survives;
 *  - a cluster that SPLITS (the student erases a stroke that was linking two
 *    pieces of writing) hands the label to whichever half kept more of the
 *    strokes that bore it; the other half is a newly separate thing and mints a
 *    fresh label;
 *  - a cluster whose strokes are erased simply stops being reported, and its
 *    label is never reissued.
 *
 * A label therefore names at most ONE cluster per turn. That is not cosmetic:
 * the gateway keys scene elements by label, so two clusters sharing one would
 * collapse into a single element (the second overwriting the first) and the
 * screenshot would carry two identical badges.
 *
 * Keyed per agent so two canvases in one tab number independently, and held in a
 * WeakMap so the registry dies with the canvas rather than growing for the life
 * of the page.
 */

export interface InkLabelRegistry {
  /** Next ordinal to mint. Never decremented — labels are not recycled. */
  next: number;
  /** strokeShapeId → label. */
  byStroke: Map<string, string>;
}

const registries = new WeakMap<object, InkLabelRegistry>();

function registryFor(owner: object): InkLabelRegistry {
  const existing = registries.get(owner);
  if (existing) return existing;
  const fresh: InkLabelRegistry = { next: 1, byStroke: new Map() };
  registries.set(owner, fresh);
  return fresh;
}

/**
 * Ordinal inside a label, or Infinity when it isn't one we minted.
 *
 * Also how "the most recent handwriting" is decided: ordinals are minted in
 * order and never recycled, so the highest ones name the clusters the student
 * wrote last. `computeInkCrops` ranks by this.
 */
export function ordinalOf(label: string): number {
  const ordinal = Number(label.slice(label.indexOf("_") + 1));
  return Number.isFinite(ordinal) ? ordinal : Number.POSITIVE_INFINITY;
}

/**
 * Label a WHOLE BOARD of clusters at once, in reading order.
 *
 * Batch rather than per-cluster because uniqueness is a property of the set: two
 * clusters can each have a valid claim on the same inherited label (the student
 * erased the stroke that linked them), and only a pass that sees both can decide
 * between them. Labelling them one at a time cannot — each call in isolation is
 * correct and the pair still collides.
 *
 * Each cluster inherits the oldest label any member carries. Where two want the
 * same one, it goes to the cluster holding the most strokes that already bore it
 * — the piece that kept the bulk of the original writing is the one an earlier
 * reference most likely meant — and ties go to the earlier cluster, which in
 * reading order is the upper-left one. Everyone else mints fresh.
 */
export function labelsForInkClusters(
  owner: object,
  clusters: string[][],
): string[] {
  const registry = registryFor(owner);

  // What each cluster would inherit on its own, and how strong its claim is.
  const claims = clusters.map((strokeIds) => {
    let label: string | null = null;
    let ordinal = Number.POSITIVE_INFINITY;
    for (const strokeId of strokeIds) {
      const known = registry.byStroke.get(strokeId);
      if (!known) continue;
      const known_ordinal = ordinalOf(known);
      if (known_ordinal < ordinal) {
        ordinal = known_ordinal;
        label = known;
      }
    }
    let strength = 0;
    if (label != null) {
      for (const strokeId of strokeIds) {
        if (registry.byStroke.get(strokeId) === label) strength += 1;
      }
    }
    return { label, strength };
  });

  /** label → index of the cluster that gets to keep it. */
  const holder = new Map<string, number>();
  claims.forEach((claim, index) => {
    if (claim.label == null) return;
    const held = holder.get(claim.label);
    // Strictly greater, so a tie leaves the earlier cluster holding it.
    if (held == null || claim.strength > claims[held].strength) {
      holder.set(claim.label, index);
    }
  });

  const labels = clusters.map((_strokeIds, index) => {
    const { label } = claims[index];
    if (label != null && holder.get(label) === index) return label;
    return `ink_${registry.next++}`;
  });

  // Recorded only once every label is settled: writing them as we go would let a
  // stroke reassigned early in the pass change what a later cluster inherits.
  labels.forEach((label, index) => {
    for (const strokeId of clusters[index])
      registry.byStroke.set(strokeId, label);
  });
  return labels;
}

/**
 * The label for a single cluster of stroke ids, in isolation.
 *
 * Callers labelling a whole board must use `labelsForInkClusters` — see its note
 * on why per-cluster calls cannot keep labels unique across a turn.
 */
export function labelForInkCluster(owner: object, strokeIds: string[]): string {
  return labelsForInkClusters(owner, [strokeIds])[0];
}

/** Drop everything known about `owner`'s ink labels (test seam). */
export function resetInkLabels(owner: object): void {
  registries.delete(owner);
}
